/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

/* ---------------------------------------------------------------------------
 * OViT — Mobile sticky Add to cart bar
 *
 * Why: on a ~500px-wide viewport the product title sits ~785px down the page,
 * the price ~850px, and the Add to cart button ~1212px — roughly 1.5 screens
 * of scrolling before a visitor can buy. Paid traffic lands on a photo with
 * no name, no price and no button in view. This keeps the name, the price and
 * the buy action on screen at all times on small viewports.
 *
 * Behaviour: mirrors the real product form button (label, disabled state) and
 * forwards clicks to it, so variant logic, sold-out states and any app that
 * hooks the form keep working. The bar hides itself whenever the real button
 * is on screen, so the customer never sees two competing buttons. Desktop is
 * untouched.
 * ------------------------------------------------------------------------ */
(function () {
  'use strict';

  if (!/\/products\//.test(window.location.pathname)) return;

  var MOBILE_QUERY = '(max-width: 749px)';
  var bar, barBtn, barTitle, barPrice, realBtn, priceEl;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function findRealButton() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    return form.querySelector('button[type="submit"], input[type="submit"]');
  }

  /* Find the visible price node inside the product info column so the sticky
     bar always shows the same figure the customer is looking at. */
  function findPriceElement() {
    var scope =
      document.querySelector('.product__info, .product-info, [class*="product__details"]') ||
      document.querySelector('main') ||
      document.body;
    var candidates = scope.querySelectorAll('span, div, p, s, strong, ins');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.children.length) continue;
      var text = (el.textContent || '').trim();
      if (/^[$€£¥]\s?[\d,]+(\.\d{2})?$/.test(text)) return el;
    }
    return null;
  }

  function injectStyles() {
    if (document.getElementById('ovit-sticky-atc-styles')) return;
    var css = [
      '.ovit-sticky-atc{',
      '  position:fixed;left:0;right:0;bottom:0;z-index:9999;',
      '  display:flex;align-items:center;gap:12px;',
      '  padding:10px 14px calc(10px + env(safe-area-inset-bottom,0px));',
      '  background:rgba(245,245,243,.97);backdrop-filter:saturate(180%) blur(12px);',
      '  -webkit-backdrop-filter:saturate(180%) blur(12px);',
      '  border-top:1px solid rgba(19,19,19,.10);',
      '  transform:translateY(110%);transition:transform .22s ease;',
      '  font-family:inherit;',
      '}',
      '.ovit-sticky-atc.is-visible{transform:translateY(0)}',
      '.ovit-sticky-atc__info{flex:1 1 auto;min-width:0;line-height:1.25}',
      '.ovit-sticky-atc__title{display:block;font-size:13px;color:#131313;',
      '  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      '.ovit-sticky-atc__price{display:block;font-size:15px;font-weight:600;color:#131313}',
      '.ovit-sticky-atc__btn{',
      '  flex:0 0 auto;min-width:152px;min-height:48px;padding:0 22px;',
      '  border:0;border-radius:999px;cursor:pointer;',
      '  background:#7f8a58;color:#f5f5f3;font-size:15px;font-weight:600;',
      '  font-family:inherit;letter-spacing:.01em;',
      '}',
      '.ovit-sticky-atc__btn[disabled]{opacity:.5;cursor:not-allowed}',
      '@media (min-width:750px){.ovit-sticky-atc{display:none!important}}',
      '@media (prefers-reduced-motion:reduce){.ovit-sticky-atc{transition:none}}'
    ].join('');
    var style = document.createElement('style');
    style.id = 'ovit-sticky-atc-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function buildBar() {
    bar = document.createElement('div');
    bar.className = 'ovit-sticky-atc';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Add to cart');

    var info = document.createElement('div');
    info.className = 'ovit-sticky-atc__info';

    barTitle = document.createElement('span');
    barTitle.className = 'ovit-sticky-atc__title';
    var heading = document.querySelector('h1');
    barTitle.textContent = heading ? heading.textContent.trim() : '';

    barPrice = document.createElement('span');
    barPrice.className = 'ovit-sticky-atc__price';

    info.appendChild(barTitle);
    info.appendChild(barPrice);

    barBtn = document.createElement('button');
    barBtn.type = 'button';
    barBtn.className = 'ovit-sticky-atc__btn';

    barBtn.addEventListener('click', function () {
      if (realBtn && !realBtn.disabled) realBtn.click();
    });

    bar.appendChild(info);
    bar.appendChild(barBtn);
    document.body.appendChild(bar);
  }

  function syncFromRealButton() {
    if (!realBtn || !barBtn) return;
    var label = (realBtn.textContent || realBtn.value || 'Add to cart').trim();
    barBtn.textContent = label || 'Add to cart';
    barBtn.disabled = !!realBtn.disabled;
    if (priceEl && barPrice) barPrice.textContent = (priceEl.textContent || '').trim();
  }

  /* Plain synchronous geometry rather than IntersectionObserver or rAF: both
     are suspended in background tabs, which makes the bar's state wrong on
     restore. A single getBoundingClientRect per scroll event is cheap. */
  function updateVisibility() {
    if (!bar || !realBtn) return;
    if (!isMobile()) {
      bar.classList.remove('is-visible');
      return;
    }
    var r = realBtn.getBoundingClientRect();
    var realButtonOnScreen = r.bottom > 0 && r.top < window.innerHeight;
    if (realButtonOnScreen) bar.classList.remove('is-visible');
    else bar.classList.add('is-visible');
  }

  function init() {
    realBtn = findRealButton();
    if (!realBtn) return;

    priceEl = findPriceElement();
    injectStyles();
    buildBar();
    syncFromRealButton();
    updateVisibility();

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    window.addEventListener('load', updateVisibility);
    document.addEventListener('visibilitychange', updateVisibility);

    /* Keep label, disabled state and price in step with variant changes. */
    var mo = new MutationObserver(function () {
      syncFromRealButton();
      updateVisibility();
    });
    mo.observe(realBtn, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class']
    });
    if (priceEl) mo.observe(priceEl, { childList: true, subtree: true, characterData: true });

    document.addEventListener('variant:change', syncFromRealButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
